import { Bot } from "lucide-react";
import { DONE_DOT, LABEL_CHIP, PRIORITY_DOT, updatedAgo } from "@/lib/board/look";
import { displayName, personInitials, shortId, type BoardRow, type Card, type Label, type Person } from "@/lib/board/types";

/**
 * One card on the board: short ID, title, labels, priority dot, assignee
 * initials, who made it and when it last changed. A card an AI teammate made
 * carries a Bot badge with its name.
 */
export function CardTile({
  board, card, assignees, labels, creator, done, onOpen, dragging,
}: {
  board: BoardRow;
  card: Card;
  assignees: Person[];
  labels: Label[];
  creator: Person | undefined;
  done: boolean;
  onOpen: () => void;
  dragging?: boolean;
}) {
  const dot = done ? DONE_DOT : PRIORITY_DOT[card.priority] ?? PRIORITY_DOT.med;
  const isBot = !!card.created_by_agent_id || creator?.employee_kind === "ai";
  const creatorName = creator ? (creator.preferred_name || creator.first_name || displayName(creator)) : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${shortId(board, card)} ${card.title}`}
      className={`block w-full select-none rounded-lg bg-white p-3 text-left text-sm shadow-sm ring-1 ring-line transition hover:shadow-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
        dragging ? "rotate-1 shadow-soft ring-teal" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${dot.dot}`} title={`${dot.label} priority`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold tracking-wide text-gray2">{shortId(board, card)}</span>
            {card.archived_at && (
              <span className="rounded bg-line px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray2">
                {card.archive_reason === "deleted" ? "Deleted" : "Archived"}
              </span>
            )}
          </div>
          <div className="font-medium leading-snug text-ink">{card.title}</div>
        </div>
      </div>

      {labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {labels.map(l => (
            <span key={l.id} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${LABEL_CHIP[l.color] ?? LABEL_CHIP.gray}`}>
              {l.name}
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray2">
          {isBot ? (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-ink px-1.5 py-0.5 font-semibold text-white" title="Created by an AI teammate">
              <Bot size={10} aria-hidden /> Bot{creatorName ? ` · ${creatorName}` : ""}
            </span>
          ) : (
            creatorName && <span className="truncate">by {creatorName}</span>
          )}
          <span aria-hidden>·</span>
          <span className="shrink-0" title={`Updated ${new Date(card.updated_at).toLocaleString()}`}>
            {updatedAgo(card.updated_at)}
          </span>
        </div>
        <div className="flex shrink-0 -space-x-1.5">
          {assignees.slice(0, 3).map(p => (
            <span
              key={p.id}
              title={displayName(p)}
              className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ring-2 ring-white ${
                p.employee_kind === "ai" ? "bg-ink text-white" : "bg-teal-mint text-teal-dark"
              }`}
            >
              {personInitials(p)}
            </span>
          ))}
          {assignees.length > 3 && (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-line text-[10px] font-semibold text-gray2 ring-2 ring-white">
              +{assignees.length - 3}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
